import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import { getPeriodById } from '../api/requests/sectionsAndPackages';
import DisplayError from '../components/info/DisplayError';
import DisplayInfo from '../components/info/DisplayInfo';
import Loading from '../components/info/Loading';
import LoadingModal from '../components/modals/LoadingModal';
import { PERIOD_STATES, PeriodType } from '../types/sectionsAndPackageTypes';
import DownloadBaseInformationDocument from './parts/DownloadBaseInformationDocument';
import DownloadLoadedInformationDocument from './parts/DownloadLoadedInformationDocument';
import PeriodsTable from './parts/PeriodsTable';
import UploadSections from './parts/UploadSections';

export interface PeriodViewProps {
  periodId: number;
}
const PeriodsList = ({ periodId }: PeriodViewProps) => {
  const { t } = useTranslation();
  const prefix = 'packagesMaintainer';

  const [periods, setPeriods] = useState<PeriodType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [loadingTitle, setLoadingTitle] = useState<string>('');
  const [loadingDesc, setLoadingDesc] = useState<string>('');
  const [isClosed, setIsClosed] = useState<boolean>(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data } = await getPeriodById(periodId);
    if (data) {
      setError(false);
      setPeriods([data]);
      setIsDisabled(data.state === PERIOD_STATES.EMPTY);
      setIsClosed(isCompletedTime(data.loadingDeadline));
    } else {
      setError(true);
    }
    setLoading(false);
  }, [periodId, setLoading, setError, setPeriods, setIsDisabled]);

  const isCompletedTime = (limitDate: string | null): boolean => {
    let timePassed = false;
    if (limitDate) {
      const date = new Date(limitDate);
      const now = new Date();
      if (now.getTime() > date.getTime()) {
        timePassed = true;
      }
    }
    return timePassed;
  };

  const beforeDownload = () => {
    setLoadingTitle(t(`${prefix}.downloads.loadingModal.title`));
    setLoadingDesc(t(`${prefix}.downloads.loadingModal.desc`));
    setOpenModal(true);
  };

  const beforeUpload = () => {
    setLoadingTitle(t(`${prefix}.upload.loadingModal.title`));
    setLoadingDesc(t(`${prefix}.upload.loadingModal.desc`));
    setOpenModal(true);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <DisplayError
        title={t(`${prefix}.loadErrorTitle`)}
        textBody={t(`${prefix}.loadErrorBody`)}
        insideCard
      />
    );
  }

  if (!periods.length) {
    return (
      <DisplayInfo
        title={t(`${prefix}.noPeriodsTitle`)}
        textBody={t(`${prefix}.noPeriodFound`)}
        insideCard
      />
    );
  }

  return (
    <React.Fragment>
      <LoadingModal
        isOpen={openModal}
        title={loadingTitle}
        description={loadingDesc}
      />
      <PeriodsTable periods={periods} />
      <Row>
        <Col>
          <DownloadBaseInformationDocument
            beforeDownload={beforeDownload}
            afterDownload={() => setOpenModal(false)}
            disabled={isClosed}
            periodId={periodId}
          />
        </Col>
        <Col>
          <DownloadLoadedInformationDocument
            beforeDownload={beforeDownload}
            afterDownload={() => setOpenModal(false)}
            disabled={isDisabled}
            periodId={periodId}
          />
        </Col>
        <Col>
          <UploadSections
            beforeUpload={beforeUpload}
            afterUpload={() => fetchData()}
            setOpenModal={(bol: boolean) => setOpenModal(bol)}
            beforeDownload={beforeDownload}
            afterDownload={() => setOpenModal(false)}
            disabled={isClosed}
            loadAgain={!isDisabled}
          />
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default PeriodsList;
